import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCloudDrizzle, FiCoffee, FiCheckCircle, FiCircle, FiArrowRight, FiClock, FiAlertTriangle } = FiIcons;

export default function Dashboard() {
  const [checklist, setChecklist] = useState([
    { id: 1, text: 'Metro Pass NFC Active', done: true },
    { id: 2, text: 'Noise-cancelling Pods', done: false },
    { id: 3, text: 'Travel Mug (Hot)', done: false },
    { id: 4, text: 'Umbrella (Light Rain Exp.)', done: false },
  ]);

  const toggleCheck = (id) => {
    setChecklist(checklist.map(item => item.id === id ? { ...item, done: !item.done } : item));
  };

  const [coffeeTime, setCoffeeTime] = useState(258); // seconds

  useEffect(() => {
    const timer = setInterval(() => {
      setCoffeeTime(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-6"
    >
      <header className="mb-8">
        <h2 className="text-3xl font-bold text-neutral-100 flex items-center gap-3">
          Morning Protocol
          <span className="h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,1)] animate-pulse"></span>
        </h2>
        <p className="text-neutral-400 mt-1 font-mono text-sm">System synchronized. Routine optimal.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Departure Card */}
        <div className="lg:col-span-2 bg-neutral-900/40 border border-neutral-800 rounded-2xl p-6 relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/5 rounded-full blur-3xl -mr-10 -mt-10 pointer-events-none transition-opacity group-hover:opacity-100 opacity-50"></div>
          
          <div className="flex justify-between items-start mb-6">
            <div>
              <span className="text-xs font-mono text-emerald-400 uppercase tracking-widest mb-2 block">Next Departure</span>
              <h3 className="text-2xl font-bold flex items-center gap-2">
                Cyberbus 204 <span className="text-neutral-500 text-lg">→</span> Central Hub
              </h3>
            </div>
            <div className="text-right">
              <span className="text-4xl font-light tracking-tighter text-emerald-100 shadow-emerald-500/20 drop-shadow-md">
                12<span className="text-2xl text-emerald-500/70">min</span>
              </span>
            </div>
          </div>

          <div className="space-y-4">
            <div className="h-1.5 w-full bg-neutral-800 rounded-full overflow-hidden">
              <motion.div 
                className="h-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.8)] object-cover"
                initial={{ width: "0%" }}
                animate={{ width: "65%" }}
                transition={{ duration: 1.5, ease: "easeOut" }}
              />
            </div>
            <div className="flex justify-between text-xs font-mono text-neutral-500">
              <span>Home Base</span>
              <span>En Route</span>
              <span>Station Alpha</span>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="bg-neutral-950/50 border border-neutral-800/80 rounded-xl p-4 flex items-center gap-4">
              <div className="p-2 bg-blue-500/10 rounded-lg text-blue-400">
                <SafeIcon icon={FiCloudDrizzle} className="text-xl" />
              </div>
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wider font-mono">Conditions</p>
                <p className="text-sm font-medium text-neutral-200">Mist, 12°C</p>
              </div>
            </div>
            <div className="bg-neutral-950/50 border border-neutral-800/80 rounded-xl p-4 flex items-center gap-4 border-l-2 border-l-amber-500/50">
              <div className="p-2 bg-amber-500/10 rounded-lg text-amber-500">
                <SafeIcon icon={FiAlertTriangle} className="text-xl" />
              </div>
              <div>
                <p className="text-xs text-neutral-500 uppercase tracking-wider font-mono">Network Alert</p>
                <p className="text-sm font-medium text-neutral-200 truncate">Minor delay on Green Line</p>
              </div>
            </div>
          </div>
        </div>

        {/* Side Widgets */}
        <div className="space-y-6">
          {/* Coffee Timer */}
          <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl p-6 flex flex-col items-center justify-center relative overflow-hidden">
             <div className="absolute inset-0 bg-gradient-to-b from-amber-500/5 to-transparent pointer-events-none"></div>
             <SafeIcon icon={FiCoffee} className="text-3xl text-amber-500/80 mb-3 drop-shadow-[0_0_8px_rgba(245,158,11,0.4)]" />
             <h4 className="text-sm font-medium text-neutral-300 mb-1">Station Cafe Prep</h4>
             <div className="text-3xl font-mono tracking-widest text-amber-400 font-light drop-shadow-[0_0_5px_rgba(245,158,11,0.5)]">
               {formatTime(coffeeTime)}
             </div>
             <p className="text-xs text-neutral-500 mt-2 font-mono uppercase">Order #8892 • Brewing</p>
          </div>

          {/* Checklist */}
          <div className="bg-neutral-900/40 border border-neutral-800 rounded-2xl p-6">
            <h4 className="text-sm font-mono uppercase tracking-widest text-emerald-400 mb-4 flex items-center justify-between">
              Departure Checklist
              <span className="text-neutral-600">{checklist.filter(c => c.done).length}/{checklist.length}</span>
            </h4>
            <div className="space-y-3">
              {checklist.map(item => (
                <div 
                  key={item.id}
                  onClick={() => toggleCheck(item.id)}
                  className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                    item.done 
                      ? 'bg-emerald-900/10 border-emerald-500/20 text-neutral-400' 
                      : 'bg-neutral-950/50 border-neutral-800 hover:border-neutral-700 text-neutral-200'
                  }`}
                >
                  <motion.div
                    initial={false}
                    animate={{ scale: item.done ? [1, 1.2, 1] : 1 }}
                    transition={{ duration: 0.3 }}
                  >
                    <SafeIcon 
                      icon={item.done ? FiCheckCircle : FiCircle} 
                      className={`text-lg ${item.done ? 'text-emerald-500 shadow-emerald-500/50' : 'text-neutral-600'}`} 
                    />
                  </motion.div>
                  <span className={`text-sm ${item.done ? 'line-through decoration-emerald-500/30' : ''}`}>
                    {item.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}